import { memo, ReactNode, useContext, useEffect, useRef, useState } from 'react'
import { DrawingTypes } from '../../drawingTools/drawing.interface'
import { Box, Stack } from '@mui/material'
import { ExtraSmallButton } from '../../../custom'
import { ChartEvents } from '../../chart.events'
import { AppContext } from '../../../../App'
import { useSelector } from 'react-redux'
import { selectActiveChartId } from '../../../../selectors/chart'
import ThinArrowUpIcon from '../../../../assets/icons/ThinArrowUpIcon'
import { DrawingToolbarMenuItem, DrawingToolbarOptionsMenu } from '../drawingToolbarMenuShared'
import {
    DRAWING_TOOLBAR_CHEVRON_CLASS,
    DRAWING_TOOLBAR_MENU_OPEN_CLASS,
    DrawingToolbarIcon,
    drawingToolbarButtonSx,
    drawingToolbarChevronButtonSx,
    drawingToolbarMenuGroupSx,
    drawingToolbarSelectedBg,
    drawingToolbarToolSlotSx
} from '../drawingToolbarShared'

export type DrawingToolMenuItem = {
    text: string,
    icon: ReactNode,
    drawingToolId: DrawingTypes,
    toolOptions: Record<string, any>,
    drawingType: DrawingTypes
    code?: string
}

export type DrawingToolMenuProps = {
    items: DrawingToolMenuItem[],
    activeToolId: DrawingTypes | null,
    onClick: (drawingType: DrawingTypes, options: Record<string, any>) => void
    disable: boolean
}

const DrawingToolMenu: React.FC<DrawingToolMenuProps> = ({ items, activeToolId, onClick, disable }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
    const appContext = useContext(AppContext)
    const activeChartId = useSelector(selectActiveChartId)
    const [active, setActive] = useState(false)
    const [previousActiveMenuItem, setPreviousActiveMenuItem] = useState<DrawingToolMenuItem>(items[0])
    const chevronButtonRef = useRef<HTMLButtonElement>(null)
    const menuOpen = Boolean(anchorEl)

    useEffect(() => {
        if (activeToolId === null || !items.find(i => i.drawingType === activeToolId)) {
            setActive(false)
        }
    }, [activeToolId])

    useEffect(() => {
        const chart = appContext.getChart(activeChartId)
        if(chart) chart.subscribeEvent(ChartEvents.REMOVE_DRAWING, setToolInActive)
        return ()=>{
            if(chart) chart.unsubscribeEvent(ChartEvents.REMOVE_DRAWING, setToolInActive)
        }
    }, [])

    useEffect(() => {
        if (activeToolId !== null) {
            const foundItem = items.find(i => i.drawingType === activeToolId)
            if (foundItem) {
                setPreviousActiveMenuItem(foundItem)
            }

        }
    }, [items, activeToolId])

    const handleClick = () => {
        if (menuOpen) {
            handleClose()
        } else {
            setAnchorEl(chevronButtonRef.current)
        }
    }

    const setToolInActive = () => {
        setActive(false)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    return (
        <Stack alignItems={'center'} sx={drawingToolbarMenuGroupSx}>
            <Box
                sx={drawingToolbarToolSlotSx}
                className={menuOpen ? DRAWING_TOOLBAR_MENU_OPEN_CLASS : undefined}
            >
                <ExtraSmallButton
                    color={active ? 'primary' : 'inherit'}
                    onClick={() => {
                        if (active) {
                            handleClick()
                        } else {
                            setActive(!active)
                            onClick(previousActiveMenuItem.drawingToolId, previousActiveMenuItem.toolOptions)
                        }
                    }}
                    sx={{
                        ...drawingToolbarButtonSx,
                        bgcolor: theme => (active ? drawingToolbarSelectedBg(theme) : 'transparent')
                    }}
                    disabled={disable}
                >
                    <DrawingToolbarIcon>{previousActiveMenuItem.icon}</DrawingToolbarIcon>
                </ExtraSmallButton>
                <ExtraSmallButton
                    ref={chevronButtonRef}
                    onClick={handleClick}
                    color="inherit"
                    disabled={disable}
                    className={DRAWING_TOOLBAR_CHEVRON_CLASS}
                    sx={drawingToolbarChevronButtonSx(menuOpen)}
                >
                    <ThinArrowUpIcon sx={{ fontSize: '12px' }} />
                </ExtraSmallButton>
            </Box>
            <DrawingToolbarOptionsMenu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleClose}
                estimatedWidth={220}
            >
                {items.map((i) => (
                    <DrawingToolbarMenuItem
                        selected={i.drawingType === activeToolId}
                        key={`split-horizontal-${i.text}`}
                        icon={i.icon}
                        label={i.text}
                        onClick={() => {
                            if (activeToolId !== i.drawingType) {
                                setActive(true)
                                onClick(i.drawingType, i.toolOptions)
                            }
                            handleClose()
                        }}
                    />
                ))}
            </DrawingToolbarOptionsMenu>
        </Stack>
    )
}

export default memo(DrawingToolMenu)
